/**
 * ┌────────────────────────────────────────────────────────────────────────────┐
 * │                         EXTRACTED & EXTENDED MODULE                        │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * This file is a TypeScript port and functional extension of the `barhandles`
 * package originally published on npm. The source code has been:
 *
 *   ✔ migrated from coffeeScript -> typeScript
 *   ✔ extended to include *source location* metadata (`loc`) for each node
 *   ✔ enhanced to more accurately identify array nodes
 *   ✔ Removed `lodash` and `handlebars` peer deps. Now uses in source lodash-style utils,
 *     and accepts handlebars `parse` method as an argument
 *   ✔ Fixed issue with all items inside `{{#if}}` blocks being marked as optional, even if unrelated
 *     to the condition
 *
 *
 * Original source attribution: npm package `barhandles`
 * https://www.npmjs.com/package/barhandles
 */

import { type parse as HandlebarsParse } from 'handlebars'
import type { JSONSchema7, JSONSchema7Definition } from 'json-schema'

type SchemaNode = {
  _type?: 'object' | 'array' | 'any'
  _optional?: boolean
  _locs?: Location[]
} & {
  [key: string]: SchemaNode | undefined
}

type ExtractOptions = {
  [helperName: string]: {
    contextParam?: number
    transmogrify?: (path: string[]) => string[]
    optional?: boolean
  }
}

type Location = {
  first_line: number
  first_column: number
  last_line: number
  last_column: number
}

type EmitCallback = (
  path: string[],
  optional: boolean,
  conditionStack: string[][],
  locs?: Location
) => void

function extract(
  template: string,
  callback: EmitCallback,
  parse: typeof HandlebarsParse,
  opts: ExtractOptions = {}
) {
  const defaultHelpers: ExtractOptions = {
    each: {
      contextParam: 0,
      transmogrify: (path) => {
        const clone = [...path]
        clone.push('#')
        return clone
      },
    },
    with: {
      contextParam: 0,
    },
    if: {
      optional: true,
    },
    unless: {
      optional: true,
    },
  }

  const helperDetails = merge({}, defaultHelpers, opts) as ExtractOptions
  const parsed = parse(template)

  const conditionStack: string[][] = [] // keep track of optional paths in {{#if}} blocks
  const blockParamStack: string[][] = [] // keep track of named params in #each blocks, i.e {{#each Stuff as |thing, idx|}}

  function extend(
    path: string[],
    subpath?: { original?: string; parts: string[] }
  ): string[] {
    if (!subpath || !subpath.original) return path

    const parts = subpath.parts

    if (subpath.original.startsWith('@root')) {
      // remove @root and use the rest as the full path, split into segments
      return parts.slice(1)
    } else if (subpath.original.startsWith('@')) {
      // context variables like @index, just return path unchanged
      return path
    } else if (subpath.original.startsWith('../')) {
      // go up one level (or two if last segment is '#') and append new parts
      const clone =
        path[path.length - 1] === '#' ? path.slice(0, -2) : path.slice(0, -1)
      return [...clone, ...parts]
    } else {
      // normal case: append parts as separate segments
      return [...path, ...parts]
    }
  }

  function visit(
    emit: EmitCallback | ((path: string[]) => void),
    path: string[],
    node: typeof parsed | hbs.AST.Node | null | undefined,
    optional = false
  ) {
    if (!node) return

    switch (node.type) {
      case 'Program': {
        const typedNode = node as hbs.AST.Program
        typedNode.body.forEach((child) => visit(emit, path, child, optional))
        break
      }

      // case 'BlockStatement': {
      //   let newPath = path
      //   const typedNode = node as hbs.AST.BlockStatement
      //   const helper = helperDetails[typedNode.path.original]

      //   typedNode.params.forEach((param) =>
      //     visit(emit, path, param, optional || (helper?.optional ?? false))
      //   )

      //   if (helper?.contextParam !== undefined) {
      //     visit(
      //       (p: string[]) => {
      //         newPath = p
      //       },
      //       path,
      //       typedNode.params[helper.contextParam]
      //     )

      //     if (helper.transmogrify) {
      //       newPath = helper.transmogrify(newPath)
      //     }
      //   }

      //   visit(
      //     emit,
      //     newPath,
      //     typedNode.program,
      //     optional || (helper?.optional ?? false)
      //   )
      //   break
      // }
      case 'BlockStatement': {
        let newPath = path
        const typedNode = node as hbs.AST.BlockStatement
        const helper = helperDetails[typedNode.path.original]
        const isConditional = ['if', 'unless'].includes(typedNode.path.original)

        // do not allow empty {{#if}} blocks
        // the handlebars parser does not throw on this, but we we should
        if (
          isConditional &&
          (!typedNode.params || typedNode.params.length === 0)
        ) {
          throw new ErrorWithLocation(
            `{{#if}} block is missing a condition`,
            typedNode.loc
              ? {
                  first_line: typedNode.loc.start.line,
                  first_column: typedNode.loc.start.column,
                  last_line: typedNode.loc.end.line,
                  last_column: typedNode.loc.end.column,
                }
              : undefined
          )
        }

        const helperParamOptional =
          optional ||
          (typedNode.params.length && helper
            ? (helper.optional ?? false)
            : false)

        // if it's an `if`, grab the path as the conditional one
        let condPath: string[] = []
        const firstParam = typedNode.params.length ? typedNode.params[0] : null
        if (isConditional && firstParam?.type === 'PathExpression') {
          condPath = extend(path, firstParam as hbs.AST.PathExpression)
          visit(emit, path, typedNode.params[0], helperParamOptional)
          conditionStack.push(condPath)
        } else {
          // not an `if` just check all the params (old behavior)
          typedNode.params.forEach((p) => visit(emit, path, p, optional))
        }

        if (helper?.contextParam !== undefined) {
          // compute the path of the context param
          visit(
            (p: string[]) => {
              newPath = p
            },
            path,
            typedNode.params[helper.contextParam],
            optional
          )
          if (helper.transmogrify) {
            newPath = helper.transmogrify(newPath)
          }

          // new: emit newPath with '#' appended to mark the block param itself as array
          // omit loc data to avoid muddying the linter info with multiple locs for the same path
          emit(newPath, optional, conditionStack)
        }

        // track the block params for #each blocks
        const blockParams = typedNode.program.blockParams || []
        blockParamStack.push(blockParams)

        visit(emit, newPath, typedNode.program, optional)

        // pop off the stack if we now that we've exited the #each block
        blockParamStack.pop()

        // if we have an inverse (i.e {{#if Foo}}...{{else}}...), visit it too
        if (typedNode.inverse) visit(emit, newPath, typedNode.inverse, optional)

        // pop off the stack if we now that we've exited the `if` block
        if (isConditional) conditionStack.pop()

        break
      }

      case 'PathExpression': {
        const typedNode = node as hbs.AST.PathExpression

        // strip leading params to get real path
        // i.e {{#each Foo as [bar, idx]}} -> ['Foo', 'bar', 'idx']
        const activeParams = blockParamStack[blockParamStack.length - 1] || []
        let parts = typedNode.parts

        // if no new named params for schema (i.e “idx”), just pass over
        if (activeParams.includes(parts[0]) && parts.length === 1) break

        // if it starts with a tracked block-param (i.e foo.Bar) drop that first segment -> Bar
        if (activeParams.includes(parts[0])) {
          parts = parts.slice(1)
        }

        // rebuild the path, extending it with the current node's parts
        const subpath = { original: typedNode.original, parts }
        const fullPath = extend(path, subpath)

        // if we're currently in an #if block with this path, its definitely optional
        const wasTested = conditionStack.some(
          (cp) =>
            cp.length === fullPath.length &&
            cp.every((seg, i) => seg === fullPath[i])
        )

        emit(
          fullPath,
          optional || wasTested,
          conditionStack,
          typedNode.loc ? getLocationFromNode(typedNode.loc) : undefined
        )
        break
      }

      case 'MustacheStatement': {
        const typedNode = node as hbs.AST.MustacheStatement
        const helper =
          'original' in typedNode.path
            ? helperDetails[typedNode.path.original]
            : undefined

        const helperParamOptional = optional || (helper?.optional ?? false)

        if (isEmpty(typedNode.params)) {
          visit(emit, path, typedNode.path, helperParamOptional)
        } else {
          typedNode.params.forEach((param) =>
            visit(emit, path, param, helperParamOptional)
          )
        }

        break
      }

      default:
        break
    }
  }

  visit(callback, [], parsed)
}

export function extractSchema(
  template: string,
  parse: typeof HandlebarsParse,
  opts?: ExtractOptions
): SchemaNode {
  const obj: SchemaNode = {}

  const callback: EmitCallback = (path, optional, conditionStack, loc) => {
    const augment = (current: SchemaNode, remaining: string[]): SchemaNode => {
      // [Original] setting optional on every segment caused parents to lose their optional status.
      // i.e Foo.Bar marked optional -> Foo.Bar.Baz -> emit w/ optional=false => Foo becomes required.
      // current._optional = '_optional' in current ? current._optional && optional : optional

      // if the last property in a path, i.e Bar in Foo.Bar
      if (
        remaining.length === 0 ||
        (remaining.length === 1 && remaining[0] === 'length')
      ) {
        // if we're trying to access .length on something, lets assume it should be an array
        // note: this may get weird if we happen to define length on object, but I don't think we will do that
        // as the pattern at the moment is to use UpperCamelCase for object properties
        if (remaining.length && remaining[0] === 'length') {
          current._type = 'array'
          return current
        }

        // if this is a required field and parent is currently optional, upgrade it
        if (!optional && '_optional' in current && current._optional) {
          current._optional = false
        } else {
          current._optional =
            '_optional' in current ? current._optional && optional : optional
        }

        current._type = 'any'

        if (loc) {
          current._locs = current._locs || []
          current._locs.push(loc)
        }
        return current
      }

      const [segment, ...rest] = remaining

      if (segment === '#') {
        current._type = 'array'
        if (!current[segment]) current[segment] = {}
        return augment(current[segment] as SchemaNode, rest)
      }

      current._type = 'object'
      if (!current[segment]) current[segment] = {}
      return augment(current[segment] as SchemaNode, rest)
    }

    augment(obj, path)

    // upgrade optional ancestors if this leaf is required and top-level (not in #if block)
    if (!optional && conditionStack.length === 0) {
      let node: typeof obj | undefined = obj
      path.forEach((segment) => {
        node = node?.[segment]
        if (!node) return
        node._optional = false
      })
    }
  }

  // extract schema by walking template AST and invoking callback for each path
  extract(template, callback, parse, opts)

  // post-process schema to promote any node with '#' child to type 'array'
  function promoteArrays(node: SchemaNode) {
    if (node['#']) {
      node._type = 'array'
      promoteArrays(node['#'])
    } else {
      if (!node._type) node._type = 'object'
      for (const key in node) {
        if (!key.startsWith('_') && typeof node[key] === 'object') {
          promoteArrays(node[key]!)
        }
      }
    }
  }

  promoteArrays(obj)
  delete obj._optional
  return obj
}
const getLocationFromNode = (
  loc: hbs.AST.SourceLocation
): Location | undefined => {
  return {
    first_line: loc.start.line,
    first_column: loc.start.column,
    last_line: loc.end.line,
    last_column: loc.end.column,
  }
}

/**
 * custom implementation of lodash.isEmpty that works for null/undefined
 */
const isEmpty = (obj: unknown): boolean => {
  if (obj === null || obj === undefined) return true
  if (Array.isArray(obj)) return obj.length === 0
  if (typeof obj === 'object') return Object.keys(obj).length === 0
  return false
}

/**
 * custom implementation of lodash.merge that works with unknown types
 */
const isObject = (value: unknown) =>
  ['object', 'function'].includes(typeof value) && value !== null

const isPlainObject = (value: unknown) => {
  if (!isObject(value)) return false
  const prototype = Object.getPrototypeOf(value)
  return prototype === null || prototype === Object.prototype
}

/*
 * custom implementation of lodash.merge that works with unknown types
 * Deep merge objects, mutating the first object.
 * Arrays and non-plain objects are overwritten, not merged.
 * Skips undefined values.
 */
function merge(
  object: Record<string, unknown>,
  ...sources: Record<string, unknown>[]
): unknown {
  for (const source of sources) {
    for (const [key, value] of Object.entries(source)) {
      if (value === undefined) continue
      if (!isPlainObject(value) && !Array.isArray(value)) object[key] = value
      else if (Array.isArray(value) && !Array.isArray(object[key]))
        object[key] = value
      else if (!isObject(object[key])) object[key] = value
      else
        merge(
          object[key] as Record<string, unknown>,
          value as Record<string, unknown>
        )
    }
  }

  return object
}

class ErrorWithLocation extends Error {
  hash: {
    loc?: Location
  }

  constructor(message: string, loc?: Location) {
    super(message)
    this.hash = {
      loc: loc,
    }
  }
}

/**
 *
 * custom function, placing here as it seems like the most appropriate place
 *
 * Validate a Handlebars prompt template for references to handlebars variables
 * against the current prompt schema.
 * Returns an array of diagnostics with errors, warnings, and info about the template.
 * @param prompt - The Handlebars template string to validate.
 * @param getLineStartPositionFromView - A function that returns the start position of a line in the view.
 * This is used to convert line numbers from the template to character positions in the view.
 * @returns An object containing arrays of errors, warnings, and info diagnostics.
 */
export const validatePromptHandlebarTemplateReferences = ({
  prompt,
  getLineStartPositionFromView,
  promptSchema,
  handlebarsParse,
}: {
  prompt: string
  getLineStartPositionFromView: (lineNumber: number) => number
  promptSchema: JSONSchema7
  handlebarsParse: typeof HandlebarsParse
}) => {
  type ErrorDiagnostic = {
    from: number
    to: number
    severity: 'error'
    message: string
  }

  type WarningDiagnostic = Omit<ErrorDiagnostic, 'severity'> & {
    severity: 'warning'
  }

  type InfoDiagnostic = Omit<ErrorDiagnostic, 'severity' | 'message'> & {
    severity: 'info'
    path: string
    type: string
    description?: string
  }

  const errors: ErrorDiagnostic[] = []
  const warnings: WarningDiagnostic[] = []
  const infos: InfoDiagnostic[] = []

  // the minimally defined shape of the data expected from the template
  const templateSchema = extractSchema(prompt, handlebarsParse)

  // determine if a property exists in JSONSchema source of truth
  const doesPathExistInSchema = (
    path: string[],
    schema: JSONSchema7
  ):
    | { exists: false }
    | {
        exists: true
        definition: JSONSchema7Definition
        guaranteed: boolean
      } => {
    let current: JSONSchema7Definition = schema
    let guaranteed = true

    for (const segment of path) {
      if (!current || typeof current !== 'object') return { exists: false }

      if (current.type === 'object' && current.properties) {
        //check property exists
        if (!(segment in current.properties)) return { exists: false }

        //check if this property is required
        if (!current.required || !current.required.includes(segment))
          guaranteed = false

        current = current.properties[segment]
      } else if (current.type === 'array' && segment === '#') {
        // items should be guaranteed on array types, but belt and suspenders + ts check
        if (!current.items) return { exists: false }
        current = current.items as JSONSchema7Definition
      } else {
        return { exists: false }
      }
    }

    return { exists: true, definition: current, guaranteed }
  }

  // recursive function to walk through the schemaNode and validate paths/types
  const validateNode = (node: typeof templateSchema, path: string[] = []) => {
    // first check if have that path in the schema whatsoever
    const result = doesPathExistInSchema([...path], promptSchema)

    // construct a user friendly path representation for messages
    const resolvedPath = [...path].join('.').replace('.#.', '[].')

    if (!result.exists && node._locs) {
      for (const loc of node._locs) {
        errors.push({
          from: getLineStartPositionFromView(loc.first_line) + loc.first_column,
          to: getLineStartPositionFromView(loc.last_line) + loc.last_column,
          severity: 'error',
          message: `Reference '${resolvedPath}' not found in prompt schema.`,
        })
      }
    }

    if (result.exists) {
      const nodeType = node._type ?? 'any'
      const nodeRequired = '_optional' in node ? !node._optional : true
      const schemaType =
        typeof result.definition === 'object'
          ? result.definition.type
          : undefined
      const schemaPropGuaranteed = result.guaranteed

      //let didWarn = false

      // if the template implied the prop exists but it may not be there, error
      if (nodeRequired && !schemaPropGuaranteed) {
        // didWarn = true
        // block the whole node range (maybe refactor this in future w/ more data in extract schema)
        warnings.push({
          from: node._locs
            ? getLineStartPositionFromView(node._locs[0].first_line) +
              node._locs[0].first_column
            : 0,
          to: node._locs
            ? getLineStartPositionFromView(
                node._locs[node._locs.length - 1].last_line
              ) + node._locs[node._locs.length - 1].last_column
            : 0,
          severity: 'warning',
          message: `Reference '${resolvedPath}' may not be defined at runtime.`,
        })
      }

      if (nodeType !== 'any') {
        const schemaTypes = (() => {
          if (typeof schemaType === 'string') return [schemaType]
          if (Array.isArray(schemaType)) return schemaType
          return []
        })()

        // if the schema doesn't specify any type, no validation to be done
        // I'm not sure we can actually arrive here in practice
        const typeMatches = schemaTypes.length
          ? schemaTypes.some((t) => t === nodeType)
          : true

        if (!typeMatches) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          // didWarn = true
          // block the whole node range (maybe refactor this in future w/ more data in extract schema)
          warnings.push({
            from: node._locs
              ? getLineStartPositionFromView(node._locs[0].first_line) +
                node._locs[0].first_column
              : 0,
            to: node._locs
              ? getLineStartPositionFromView(
                  node._locs[node._locs.length - 1].last_line
                ) + node._locs[node._locs.length - 1].last_column
              : 0,
            severity: 'warning',
            message: `Reference '${resolvedPath}' is of type '${nodeType}' in template but of type '${schemaTypes.join(
              '|'
            )}' in prompt schema.`,
          })
        }
      }

      // push the info even if we did warn so that the user can see the type/description
      if (node._locs /*&& !didWarn*/) {
        const { definition } = result
        for (const loc of node._locs) {
          infos.push({
            from:
              getLineStartPositionFromView(loc.first_line) + loc.first_column,
            to: getLineStartPositionFromView(loc.last_line) + loc.last_column,
            severity: 'info',
            path: resolvedPath,
            type: getTypeStringFrontJsonSchemaDef(definition),
            description:
              typeof definition === 'object'
                ? definition.description
                : undefined,
          })
        }
      }
    }

    //keep recursing
    for (const key of Object.keys(node)) {
      if (key.startsWith('_')) continue // skip metadata keys
      const child = node[key]
      if (typeof child === 'object' && child !== null && '_type' in child)
        validateNode(child, [...path, key])
    }
  }

  validateNode(templateSchema)

  return { errors, infos, warnings }
}

function getTypeStringFrontJsonSchemaDef(def: JSONSchema7Definition): string {
  if (typeof def === 'boolean') return 'any'
  if (Array.isArray(def.type)) return def.type.join('|')
  if (def.$ref) return def.$ref.split('/').pop() ?? def.$ref
  if (def.enum) return def.enum.map((v) => JSON.stringify(v)).join('|')
  if (def.const) return JSON.stringify(def.const)
  if (def.type) return def.type
  if (def.anyOf) return def.anyOf.map(getTypeStringFrontJsonSchemaDef).join('|')
  if (def.oneOf) return def.oneOf.map(getTypeStringFrontJsonSchemaDef).join('|')
  if (def.allOf) return def.allOf.map(getTypeStringFrontJsonSchemaDef).join('&')
  return 'any'
}
